import React, { useState, useMemo } from 'react';
import { Button, Select, DatePicker, Tabs, Tag, Progress } from 'antd';
import {
  SyncOutlined,
  SettingOutlined,
  CheckCircleFilled,
  WarningFilled,
  ExclamationCircleFilled,
  DownloadOutlined,
} from '@ant-design/icons';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import './Settlements.scss';

const { Option } = Select;
const { RangePicker } = DatePicker;

// Reusable Status Card Component
const StatusCard = ({ title, value, subtitle, amount, icon, variant = 'default' }) => {
  return (
    <div className={`status-card status-card-${variant}`}>
      <div className="status-card-header">
        <h4 className="status-card-title">{title}</h4>
        <div className={`status-card-icon icon-${variant}`}>{icon}</div>
      </div>
      <div className="status-card-value">{value}</div>
      <div className="status-card-footer">
        <span className={`status-subtitle ${variant}`}>{subtitle}</span>
        {amount && <span className="status-amount">{amount}</span>}
      </div>
    </div>
  );
};

// Reusable Variance Card Component
const VarianceCard = ({ title, cases, amount, status, variant = 'default' }) => {
  const getBorderColor = () => {
    switch (variant) {
      case 'error':
        return '#ff4d4f';
      case 'warning':
        return '#faad14';
      case 'success':
        return '#52c41a';
      case 'gray':
        return '#8c8c8c';
      default:
        return '#8c8c8c';
    }
  };

  return (
    <div className="variance-card" style={{ borderLeftColor: getBorderColor() }}>
      <h4 className="variance-title">{title}</h4>
      <div className="variance-cases">{cases}</div>
      <div className={`variance-amount ${variant}`}>{amount}</div>
      <Tag className={`variance-tag tag-${variant}`}>{status}</Tag>
    </div>
  );
};

// Reusable Alert Component
const AlertCard = ({ message, subtitle, variant = 'info' }) => {
  const getIcon = () => {
    switch (variant) {
      case 'error':
        return <ExclamationCircleFilled />;
      case 'warning':
        return <WarningFilled />;
      case 'success':
        return <CheckCircleFilled />;
      default:
        return <ExclamationCircleFilled />;
    }
  };

  return (
    <div className={`alert-card alert-${variant}`}>
      <div className="alert-icon">{getIcon()}</div>
      <div className="alert-content">
        <div className="alert-message">{message}</div>
        {subtitle && <div className="alert-subtitle">{subtitle}</div>}
      </div>
    </div>
  );
};

// Cell Renderer for Issue Type
const IssueTypeRenderer = (params) => {
  const value = params.value;
  let className = '';
  
  if (value === 'Overcharged') className = 'tag-error';
  else if (value === 'Duplicate Charge') className = 'tag-error';
  else if (value === 'Undercharged') className = 'tag-warning';
  else if (value === 'Expired Uncharged') className = 'tag-gray';
  else if (value === 'Double Payment (VCC + NEFT)') className = 'tag-error';
  
  return <span className={`issue-type-tag ${className}`}>{value}</span>;
};

// Cell Renderer for Variance
const VarianceRenderer = (params) => {
  const value = params.value;
  const isPositive = value && value.includes('+');
  const isNegative = value && value.includes('-');
  
  let color = '#262626';
  if (isPositive) color = '#ff4d4f';
  else if (isNegative) color = '#faad14';
  
  return <span style={{ color, fontWeight: 600 }}>{value}</span>;
};

// Cell Renderer for Status
const StatusRenderer = (params) => {
  const value = params.value;
  let className = 'status-tag-';
  
  if (value === 'investigating') className += 'info';
  else if (value === 'recovery-initiated') className += 'info';
  else if (value === 'pending') className += 'warning';
  else if (value === 'reissue-required') className += 'info';
  else if (value === 'escalated') className += 'error';
  
  return <Tag className={className}>{value}</Tag>;
};

// Main Component
const Settlements = () => {
  const [activeTab, setActiveTab] = useState('vcc');
  const [reconciliationPeriod, setReconciliationPeriod] = useState('today');

  // VCC Reconciliation Data
  const vccData = {
    statusCards: [
      {
        title: 'Mid-Office Bookings',
        value: '2,847',
        subtitle: 'Data synced successfully',
        amount: '₹2,45,67,890',
        icon: <CheckCircleFilled />,
        variant: 'success',
      },
      {
        title: 'VCCs Charged',
        value: '2,681',
        subtitle: '94.2% success rate',
        amount: '₹2,31,42,952',
        icon: <CheckCircleFilled />,
        variant: 'success',
      },
      {
        title: 'Pending Charges',
        value: '166',
        subtitle: 'Awaiting charge',
        amount: '₹14,24,938',
        icon: <WarningFilled />,
        variant: 'warning',
      },
    ],
    varianceCards: [
      {
        title: 'Overcharged',
        cases: '3 cases',
        amount: '₹12,400 excess',
        status: 'Failed',
        variant: 'error',
      },
      {
        title: 'Undercharged',
        cases: '5 cases',
        amount: '₹8,750 shortfall',
        status: 'Pending',
        variant: 'warning',
      },
      {
        title: 'Duplicate Charges',
        cases: '2 cases',
        amount: '₹24,600 recovery needed',
        status: 'Failed',
        variant: 'error',
      },
      {
        title: 'Expired Uncharged',
        cases: '6 cases',
        amount: '₹45,300 reissue required',
        status: 'Expired',
        variant: 'gray',
      },
      {
        title: 'Double Payment (VCC + NEFT)',
        cases: '1 case',
        amount: '₹15,600 escalated',
        status: 'Failed',
        variant: 'error',
      },
      {
        title: 'Resolved This Cycle',
        cases: '8 cases',
        amount: '₹38,900 recovered',
        status: 'Charged',
        variant: 'success',
      },
    ],
    alerts: [
      {
        message: 'High Priority: 2 duplicate payments detected',
        subtitle: 'BK12889 and BK12880 require immediate attention',
        variant: 'error',
      },
      {
        message: 'Medium: 6 VCCs expiring in 48 hours',
        subtitle: 'Total exposure: ₹45,300',
        variant: 'warning',
      },
      {
        message: 'FYI: New Bloom Hotels statement received',
        subtitle: 'Ready for reconciliation',
        variant: 'success',
      },
    ],
  };

  // Consolidated VCC Data
  const consolidatedData = {
    statusCards: [
      {
        title: 'Total Transactions',
        value: '5,234',
        subtitle: 'All sources combined',
        amount: '₹4,78,32,450',
        icon: <CheckCircleFilled />,
        variant: 'success',
      },
      {
        title: 'Reconciled',
        value: '4,987',
        subtitle: '95.3% matched',
        amount: '₹4,56,12,340',
        icon: <CheckCircleFilled />,
        variant: 'success',
      },
      {
        title: 'Unreconciled',
        value: '247',
        subtitle: 'Requires attention',
        amount: '₹22,20,110',
        icon: <WarningFilled />,
        variant: 'warning',
      },
    ],
    alerts: [
      {
        message: 'High Priority: 15 cross-platform discrepancies detected',
        subtitle: 'Multiple booking sources show variance',
        variant: 'error',
      },
      {
        message: 'Medium: Monthly reconciliation due in 2 days',
        subtitle: 'Complete pending reviews',
        variant: 'warning',
      },
      {
        message: 'FYI: Bank statement auto-imported successfully',
        subtitle: 'Ready for matching',
        variant: 'success',
      },
    ],
  };

  // NEFT Reconciliation Data
  const neftData = {
    statusCards: [
      {
        title: 'NEFT Payments Sent',
        value: '1,456',
        subtitle: 'Successfully processed',
        amount: '₹8,95,45,230',
        icon: <CheckCircleFilled />,
        variant: 'success',
      },
      {
        title: 'Bank Confirmed',
        value: '1,423',
        subtitle: '97.7% confirmation rate',
        amount: '₹8,78,32,450',
        icon: <CheckCircleFilled />,
        variant: 'success',
      },
      {
        title: 'Pending Confirmation',
        value: '33',
        subtitle: 'Awaiting bank update',
        amount: '₹17,12,780',
        icon: <WarningFilled />,
        variant: 'warning',
      },
    ],
    alerts: [
      {
        message: 'High Priority: 3 NEFT payments failed',
        subtitle: 'Invalid account details - vendor follow-up required',
        variant: 'error',
      },
      {
        message: 'Medium: 5 payments pending for 48+ hours',
        subtitle: 'Bank confirmation overdue',
        variant: 'warning',
      },
      {
        message: 'FYI: Bulk payment file processed successfully',
        subtitle: '156 payments queued for tomorrow',
        variant: 'success',
      },
    ],
  };

  // Action Required Table Data
  const actionTableData = [
    {
      bookingId: 'BK12891',
      hotel: 'Radisson Blu Jaipur',
      amount: '₹6,200',
      issueType: 'Overcharged',
      variance: '+₹600',
      daysPending: '3 days',
      assignedTo: 'Priya S.',
      status: 'investigating',
    },
    {
      bookingId: 'BK12889',
      hotel: 'Holiday Inn Mumbai',
      amount: '₹9,500',
      issueType: 'Duplicate Charge',
      variance: '+₹9,500',
      daysPending: '5 days',
      assignedTo: 'Amit K.',
      status: 'recovery-initiated',
    },
    {
      bookingId: 'BK12885',
      hotel: 'Courtyard Marriott Pune',
      amount: '₹8,100',
      issueType: 'Undercharged',
      variance: '-₹300',
      daysPending: '2 days',
      assignedTo: 'Rahul M.',
      status: 'pending',
    },
    {
      bookingId: 'BK12883',
      hotel: 'Novotel Goa',
      amount: '₹11,200',
      issueType: 'Expired Uncharged',
      variance: '-₹11,200',
      daysPending: '8 days',
      assignedTo: 'Sneha R.',
      status: 'reissue-required',
    },
    {
      bookingId: 'BK12880',
      hotel: 'Four Points Bangalore',
      amount: '₹7,800',
      issueType: 'Double Payment (VCC + NEFT)',
      variance: '+₹7,800',
      daysPending: '12 days',
      assignedTo: 'Vikram S.',
      status: 'escalated',
    },
  ];

  const columnDefs = useMemo(
    () => [
      {
        field: 'bookingId',
        headerName: 'Booking ID',
        width: 140,
        cellRenderer: (params) => (
          <a className="booking-link">{params.value}</a>
        ),
      },
      {
        field: 'hotel',
        headerName: 'Hotel',
        width: 200,
      },
      {
        field: 'amount',
        headerName: 'Amount',
        width: 120,
      },
      {
        field: 'issueType',
        headerName: 'Issue Type',
        width: 200,
        cellRenderer: IssueTypeRenderer,
      },
      {
        field: 'variance',
        headerName: 'Variance',
        width: 130,
        cellRenderer: VarianceRenderer,
      },
      {
        field: 'daysPending',
        headerName: 'Days Pending',
        width: 130,
      },
      {
        field: 'assignedTo',
        headerName: 'Assigned To',
        width: 130,
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 160,
        cellRenderer: StatusRenderer,
      },
      {
        headerName: 'Actions',
        width: 100,
        cellRenderer: () => (
          <a className="action-link">View</a>
        ),
      },
    ],
    []
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
    }),
    []
  );

  const getCurrentData = () => {
    switch (activeTab) {
      case 'vcc':
        return vccData;
      case 'consolidated':
        return consolidatedData;
      case 'neft':
        return neftData;
      default:
        return vccData;
    }
  };

  const currentData = getCurrentData();

  return (
    <div className="settlements-page-wrapper">
    <div className="settlements-reconciliation-container">
      {/* Header */}
      <div className="settlements-header">
        <h1 className="settlements-title">Settlements & Reconciliation</h1>
        <div className="settlements-header-actions">
          <div className="sync-info">Last Sync: 2 hours ago</div>
          <Button icon={<SettingOutlined />} size="large">
            Auto-Recon: Enabled
          </Button>
          <Button type="primary" icon={<SyncOutlined />} size="large">
            Sync Now
          </Button>
        </div>
      </div>

      {/* Reconciliation Period Selector */}
      <div className="period-selector-section">
        <div className="period-selector-left">
          <span className="period-label">Reconciliation Period:</span>
          <Select
            value={reconciliationPeriod}
            onChange={setReconciliationPeriod}
            size="large"
            style={{ width: 200 }}
          >
            <Option value="today">Today (Default)</Option>
            <Option value="yesterday">Yesterday</Option>
            <Option value="last7days">Last 7 Days</Option>
            <Option value="last30days">Last 30 Days</Option>
            <Option value="custom">Custom Range</Option>
          </Select>
        </div>
        <div className="period-selector-right">
          <span className="date-range-label">to</span>
          <RangePicker size="large" format="DD/MM/YYYY" />
        </div>
      </div>

      {/* Tabs */}
      <div className="reconciliation-tabs">
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: 'vcc',
              label: 'VCC Reconciliation',
            },
            {
              key: 'consolidated',
              label: 'Consolidated VCC',
            },
            {
              key: 'neft',
              label: 'NEFT Reconciliation',
            },
          ]}
        />
      </div>

      {/* Status Cards */}
      <div className="status-cards-grid">
        {currentData.statusCards.map((card, index) => (
          <StatusCard key={index} {...card} />
        ))}
      </div>

      {/* VCC Reconciliation Specific Content */}
      {activeTab === 'vcc' && (
        <>
          {/* Match Status Summary */}
          <div className="match-status-section">
            <h3 className="section-title">Match Status Summary</h3>
            <div className="progress-bar-container">
              <Progress
                percent={94.2}
                strokeColor="#52c41a"
                trailColor="#e8e8e8"
                showInfo={false}
                strokeWidth={20}
              />
              <div className="progress-label">
                Successfully Charged: 2,681 (94.2%)
              </div>
            </div>

            {/* Summary Cards */}
            <div className="summary-cards-grid">
              <div className="summary-card">
                <div className="summary-label">Total Bookings</div>
                <div className="summary-value">3,200</div>
              </div>
              <div className="summary-card">
                <div className="summary-label">VCC Issued</div>
                <div className="summary-value">2,847</div>
                <div className="summary-amount">₹2,45,67,890</div>
              </div>
              <div className="summary-card">
                <div className="summary-label">VCC Charged</div>
                <div className="summary-value">2,681 (94.2%)</div>
                <div className="summary-amount">₹2,31,42,952</div>
              </div>
              <div className="summary-card">
                <div className="summary-label">Cashback Earned</div>
                <div className="summary-value">₹1,24,680</div>
                <div className="summary-subtitle">November 2025</div>
              </div>
            </div>
          </div>

          {/* Variance Analysis */}
          <div className="variance-section">
            <h3 className="section-title">Variance Analysis</h3>
            <div className="variance-cards-grid">
              {vccData.varianceCards.map((card, index) => (
                <VarianceCard key={index} {...card} />
              ))}
            </div>
          </div>

          {/* Action Required Table */}
          <div className="action-required-section">
            <div className="action-header">
              <h3 className="section-title">Action Required</h3>
              <div className="action-buttons">
                <Button size="large">Bulk Approve</Button>
                <Button size="large">Initiate Recovery</Button>
                <Button icon={<DownloadOutlined />} size="large">
                  Export
                </Button>
              </div>
            </div>
            <div className="action-table">
              <div className="ag-theme-alpine" style={{ height: '400px', width: '100%' }}>
                <AgGridReact
                  rowData={actionTableData}
                  columnDefs={columnDefs}
                  defaultColDef={defaultColDef}
                  animateRows={true}
                  pagination={false}
                />
              </div>
            </div>
          </div>
        </>
      )}

      {/* Smart Alerts - Common for all tabs */}
      <div className="smart-alerts-section">
        <h3 className="section-title">Smart Alerts</h3>
        <div className="alerts-list">
          {currentData.alerts.map((alert, index) => (
            <AlertCard key={index} {...alert} />
          ))}
        </div>
      </div>
    </div>
    </div>
  );
};

export default Settlements;